import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FilterInput, FilterInputType, FilterTypeOption, FilterValue, Icon } from "@evergis/ui";
import { AggregationFunction, AttributeDefinitionDc, AttributeType } from "@evergis/api";

// TODO: вынести в общие хуки
import { useGetValueOptions } from "../../../components/LayerFilter/hooks/useGetValueOptions";
import { useTableSpecialFilter } from "../../../hooks/table/useTableSpecialFilter";
import { getComparisonOperatorOptions } from "../../../components/LayerFilter/utils/getComparisonOperatorOptions";
import { getDateFilter } from "../../../ducks/dateFilter/selectors";
import { getTableFilterConditions } from "../../../ducks/table/selectors";
import { isNumberAttribute } from "../../../interfaces/service";
import { formatFilterValue } from "../../../utils/formatFilterValue";

import { api } from "../../../evergis/api";

import { TableFilterState } from "../types";

interface TableFilterProps {
  layerName: string;
  attributeType: AttributeType;
  filters: Record<string, TableFilterState>;
  setFilters: (value: Record<string, TableFilterState>) => void;
  filterKey: string;
  filterValue: AttributeDefinitionDc;
}

export const TableFilter: FC<TableFilterProps> = ({
  layerName,
  attributeType,
  filters = {},
  setFilters,
  filterKey,
  filterValue,
}) => {
  const { t } = useTranslation("table");
  const [min, setMin] = useState<number | Date | undefined>();
  const [max, setMax] = useState<number | Date | undefined>();
  const getValueOptions = useGetValueOptions(layerName, filterKey);
  const inputValue = filters?.[layerName]?.[filterKey] ? filters[layerName][filterKey].value : null;
  const dateFilter = useSelector(getDateFilter);
  const isDateFilterApplied = dateFilter?.layers?.[layerName]?.includes(filterKey);
  const options = useMemo(() => getComparisonOperatorOptions(attributeType, true), [attributeType]);
  const [specialFilter, setSpecialFilter] = useTableSpecialFilter();
  const tableFilterConditions = useSelector(getTableFilterConditions);

  const getDefaultOption: FilterTypeOption = useMemo(() => {
    return filters?.[layerName]?.[filterKey]?.filterType || options[0];
  }, [filterKey, filters, layerName, options]);

  const [selectedOption, setSelectedOption] = useState<FilterTypeOption | null>(getDefaultOption);

  const filterType: FilterInputType = isNumberAttribute(attributeType)
    ? "number"
    : attributeType === AttributeType.DateTime
    ? "date"
    : "string";

  const onShown = useCallback(async () => {
    const isDate = attributeType === AttributeType.DateTime;

    const statistic = await api.statistic.getStatistic({
      name: layerName,
      types: [AggregationFunction.Min, AggregationFunction.Max],
      attribute: filterKey,
    });

    const { statisticsResult } = statistic;

    if (statisticsResult?.length) {
      setMin(formatFilterValue(statisticsResult.find(stat => stat.type === AggregationFunction.Min)?.value, isDate));
      setMax(formatFilterValue(statisticsResult.find(stat => stat.type === AggregationFunction.Max)?.value, isDate));
    }
  }, [attributeType, layerName, filterKey]);

  const onChange = useCallback(
    (option: FilterTypeOption, value: FilterValue) => {
      setSelectedOption(option);

      if (value === undefined || (Array.isArray(value) && value[0] === undefined && value[1] === undefined)) {
        return;
      }

      setFilters({
        ...filters,
        [layerName]: {
          ...filters?.[layerName],
          [filterKey]: {
            value:
              !filters?.[layerName]?.[filterKey] || filters?.[layerName]?.[filterKey].filterType.value === option?.value
                ? value
                : null,
            filterType: option,
            dataType: filterValue.type,
            isInactive: option?.inactive,
          },
        },
      });
    },
    [filterKey, filterValue.type, filters, layerName, setFilters],
  );

  const openSpecialFilter = useCallback(() => {
    setSpecialFilter({
      active: true,
      layerName,
      attribute: filterKey,
      attributeType,
      filterType: selectedOption,
      tableFilterCondition: tableFilterConditions?.[layerName],
      selection: undefined,
    });
  }, [attributeType, filterKey, layerName, selectedOption, setSpecialFilter, tableFilterConditions]);

  const onInputClick = useCallback(
    (_, option?: FilterTypeOption | null) => {
      if (!option) {
        return;
      }

      if (
        (option.checkboxes || (option.range && option.icon !== "date" && option.icon !== "date_outside")) &&
        !specialFilter?.active
      ) {
        openSpecialFilter();
      }
    },
    [specialFilter?.active, openSpecialFilter],
  );

  useEffect(() => {
    if (
      selectedOption &&
      ((selectedOption.range && selectedOption.icon !== "date" && selectedOption.icon !== "date_outside") ||
        selectedOption.checkboxes) &&
      !specialFilter?.active
    ) {
      openSpecialFilter();
    }
  }, [selectedOption]);

  return (
    <FilterInput
      fromTable
      type={filterType}
      options={options}
      defaultOption={getDefaultOption.value}
      selectedOption={selectedOption}
      zIndex={101}
      value={inputValue as FilterValue}
      asyncCallback={getValueOptions}
      menuTitle={t("compare")}
      clearTitle={t("clearSingleFilter")}
      min={min}
      max={max}
      onShown={onShown}
      onChange={onChange}
      disabled={isDateFilterApplied}
      prefix={isDateFilterApplied ? <Icon kind="date_filter" /> : undefined}
      onInputClick={onInputClick}
      active={specialFilter?.active && specialFilter.attribute === filterKey}
      checkboxSelectedText={t("checkboxSelected")}
      menuMaxHeight="500px"
    />
  );
};
